import { useRef, useState } from 'react';
import { Box, Divider, IconButton, TextField } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { useQuery } from '@tanstack/react-query';

import { SAvatar, SButton, SText } from '@setvi/shared/components';
import { EditPenIcon, TrashIconV2 } from '@setvi/shared/components/sicons';
import { SConfirmDialogV2 } from '@setvi/shared/components/sdialogs';
import { Types } from '@setvi/shared/components/sdialogs/sconfirm/helper';
import { useDialog } from '@setvi/shared/providers';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { getInitials } from 'Utils/getInitials';
import { getInternalNotesQuery, InternalNote } from 'Services/Query/Auto-RFQ';

const DEFAULT_USER_PHOTO =
  'https://setvi.azureedge.net/publicdev/DefaultUserPhoto.png';

const resolveAuthorPhoto = (url?: string) =>
  url && url !== DEFAULT_USER_PHOTO ? url : undefined;

const authorInitials = (
  note: Pick<InternalNote, 'authorName' | 'authorEmail'>
) => {
  const [firstName, ...rest] = (note.authorName || '').trim().split(/\s+/);
  const lastName = rest.join(' ');
  return getInitials({
    firstName,
    lastName,
    email: note.authorEmail
  });
};

const DELETE_INTERNAL_NOTE_DIALOG_ID = 'delete-internal-note';

interface InternalNoteDialogProps {
  rfqItemId?: string;
  onClose: () => void;
  onSave: (content: string) => void | Promise<void>;
  onEdit?: (note: InternalNote, content: string) => void | Promise<void>;
  onDelete?: (note: InternalNote) => void | Promise<void>;
}

const MAX_NOTE_LENGTH = 500;

const relativeTime = (iso: string) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return '';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

interface NoteItemProps {
  note: InternalNote;
  isOwner: boolean;
  isEditing: boolean;
  editContent: string;
  onEditContentChange: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete?: () => void;
}

const NoteItem = ({
  note,
  isOwner,
  isEditing,
  editContent,
  onEditContentChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete
}: NoteItemProps) => {
  const trimmed = editContent.trim();
  const hasContentChanged = trimmed && trimmed !== note.content.trim();

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SAvatar
            src={resolveAuthorPhoto(note.authorPhoto)}
            alt={note.authorName}
            value={authorInitials(note)}
            style={{ width: 28, height: 28, fontSize: 12 }}
          />
          <SText
            size="sm"
            weight="semibold"
            fontColor="#20262D"
            title={note.authorName || '—'}
          />
          <SText size="sm" fontColor="#5D7083" title="•" />
          <SText
            size="sm"
            fontColor="#5D7083"
            title={relativeTime(note.createdAt)}
          />
        </Box>
        {isOwner && !isEditing && (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconButton size="small" onClick={onStartEdit}>
              <EditPenIcon htmlColor="#5D7083" />
            </IconButton>
            {onDelete && (
              <IconButton size="small" onClick={onDelete}>
                <TrashIconV2 htmlColor="#FA2020" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {isEditing ? (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            autoFocus
            variant="outlined"
            value={editContent}
            onChange={e =>
              onEditContentChange(e.target.value.slice(0, MAX_NOTE_LENGTH))
            }
            inputProps={{ maxLength: MAX_NOTE_LENGTH }}
            inputRef={(el: HTMLTextAreaElement | null) => {
              if (el) {
                const end = el.value.length;
                el.setSelectionRange(end, end);
              }
            }}
          />
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
            <SText
              size="sm"
              fontColor="#AFBAC6"
              title={`${editContent.length}/${MAX_NOTE_LENGTH}`}
            />
          </Box>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 12
            }}>
            <SButton
              variant="text"
              color="default"
              onClick={onCancelEdit}
              style={{ color: '#5D7083' }}>
              Cancel
            </SButton>
            <SButton
              variant="contained"
              color="primary"
              disabled={!hasContentChanged}
              onClick={onSaveEdit}
              style={{ borderRadius: 36, padding: '6px 20px' }}>
              Save
            </SButton>
          </Box>
        </Box>
      ) : (
        <SText
          size="sm"
          fontColor="#20262D"
          noLineClamp
          title={note.content}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        />
      )}
    </Box>
  );
};

const InternalNoteDialog = ({
  rfqItemId,
  onClose,
  onSave,
  onEdit,
  onDelete
}: InternalNoteDialogProps) => {
  const [note, setNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const trimmed = note.trim();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { user } = useAppContext();
  const { openDialog, closeDialog } = useDialog();

  const { data: notes = [], isLoading } = useQuery({
    ...getInternalNotesQuery(rfqItemId || ''),
    enabled: !!rfqItemId
  });

  const isNoteOwner = (n: InternalNote) => {
    if (!user) return false;
    if (n.authorEmail && user.Email && n.authorEmail === user.Email)
      return true;
    if (n.authorId && String(user.ID) === String(n.authorId)) return true;
    return false;
  };

  // Unique authors for the stacked-avatar row under the counter.
  const uniqueAuthors = (() => {
    const seen = new Set<string>();
    const out: InternalNote[] = [];
    notes.forEach(n => {
      const key = n.authorId || n.authorEmail || n.authorName;
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(n);
    });
    return out;
  })();

  const handleInputRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
  };

  const handleSave = async () => {
    if (!trimmed) return;
    await onSave(trimmed);
    setNote('');
  };

  const startEdit = (n: InternalNote) => {
    setEditingNoteId(n._id);
    setEditingContent(n.content);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const saveEdit = async (n: InternalNote) => {
    const next = editingContent.trim();
    if (!next || next === n.content.trim()) {
      cancelEdit();
      return;
    }
    if (onEdit) await onEdit(n, next);
    cancelEdit();
  };

  const requestDelete = (n: InternalNote) => {
    if (!onDelete) return;
    openDialog(
      <SConfirmDialogV2
        type={Types.delete}
        title="Delete Internal Note?"
        description="This note will be permanently removed for all collaborators."
        confirmText="Delete Note"
        closeText="Cancel"
        onClose={() => closeDialog(DELETE_INTERNAL_NOTE_DIALOG_ID)}
        onConfirm={async () => {
          await onDelete(n);
          closeDialog(DELETE_INTERNAL_NOTE_DIALOG_ID);
        }}
      />,
      { id: DELETE_INTERNAL_NOTE_DIALOG_ID }
    );
  };

  return (
    <Box style={{ width: 420, padding: 20 }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8
        }}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <SText
            size="lg"
            weight="bold"
            fontColor="#20262D"
            title="Internal Note"
          />
          <SText
            size="sm"
            fontColor="#5D7083"
            noLineClamp
            title="Private to your team. Use it for vendor notes, pricing flags, or selection rationale."
          />
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon htmlColor="#5D7083" style={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {isLoading && (
        <Box
          style={{
            marginTop: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8
          }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Skeleton variant="circle" width={28} height={28} />
            <Skeleton variant="rect" width={90} height={12} />
            <Skeleton variant="rect" width={60} height={10} />
          </Box>
          <Skeleton variant="rect" width="95%" height={12} />
          <Skeleton variant="rect" width="80%" height={12} />
        </Box>
      )}

      {!isLoading && notes.length > 0 && (
        <Box
          style={{
            marginTop: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            maxHeight: 320,
            overflowY: 'auto'
          }}>
          {notes.map(n => (
            <Box
              key={n._id}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <NoteItem
                note={n}
                isOwner={isNoteOwner(n)}
                isEditing={editingNoteId === n._id}
                editContent={editingContent}
                onEditContentChange={setEditingContent}
                onStartEdit={() => startEdit(n)}
                onCancelEdit={cancelEdit}
                onSaveEdit={() => saveEdit(n)}
                onDelete={onDelete ? () => requestDelete(n) : undefined}
              />
              <Divider />
            </Box>
          ))}
        </Box>
      )}

      {!isLoading && notes.length === 0 && (
        <Box
          style={{
            marginTop: 16,
            marginBottom: 8,
            display: 'flex',
            justifyContent: 'center'
          }}>
          <SText
            size="sm"
            fontColor="#AFBAC6"
            title="No notes yet - be the first to add one."
          />
        </Box>
      )}

      <Box style={{ marginTop: 12 }}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          autoFocus
          variant="outlined"
          placeholder="Leave a note..."
          value={note}
          onChange={e => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))}
          inputProps={{ maxLength: MAX_NOTE_LENGTH }}
          inputRef={handleInputRef}
        />

        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 6,
            marginTop: 4
          }}>
          <SText
            size="sm"
            fontColor="#AFBAC6"
            title={`${note.length}/${MAX_NOTE_LENGTH}`}
          />
          {uniqueAuthors.length > 0 && (
            <Box style={{ display: 'flex' }}>
              {uniqueAuthors.slice(0, 3).map((a, i) => (
                <SAvatar
                  key={a.authorId || a.authorEmail || a.authorName}
                  src={resolveAuthorPhoto(a.authorPhoto)}
                  alt={a.authorName}
                  value={authorInitials(a)}
                  style={{
                    width: 24,
                    height: 24,
                    fontSize: 11,
                    marginLeft: i === 0 ? 0 : -6,
                    border: '2px solid #fff'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 12,
          marginTop: 16
        }}>
        <SButton
          variant="text"
          color="default"
          onClick={onClose}
          style={{ color: '#5D7083' }}>
          Cancel
        </SButton>
        <SButton
          variant="contained"
          color="primary"
          disabled={!trimmed}
          onClick={handleSave}
          style={{ borderRadius: 36, padding: '6px 20px' }}>
          Save
        </SButton>
      </Box>
    </Box>
  );
};

export default InternalNoteDialog;
